/**
 * Overlay2 - Experimental compatibility wrapper around Waypoint's sdf-floating-pane
 *
 * This is a parallel implementation to test wrapping Waypoint components while
 * maintaining the existing Overlay API. Use feature flags to test this in apps
 * before replacing the original Overlay component.
 */

import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SdfFloatingPane } from '@waypoint/react-components';
import { tabbable } from 'tabbable';
import logger from '../util/logger';

// Types for Overlay2 API
export type Placement2 =
  | 'auto' | 'auto-start' | 'auto-end'
  | 'top' | 'top-start' | 'top-end'
  | 'bottom' | 'bottom-start' | 'bottom-end'
  | 'right' | 'right-start' | 'right-end'
  | 'left' | 'left-start' | 'left-end';

// Internal type - not exported to avoid conflicts
type DOMContainer2 =
  | HTMLElement
  | (() => HTMLElement | null)
  | { current: HTMLElement | null }
  | null;

export interface IOverlay2InjectedProps {
  'aria-labelledby'?: string;
  arrowProps?: { ref: any; style: object };
  placement?: Placement2;
  ref?: React.RefCallback<HTMLElement>;
  show?: boolean;
  style?: React.CSSProperties;
  [prop: string]: any;
}

export type Overlay2Children =
  | React.ReactElement<IOverlay2InjectedProps>
  | ((injected: IOverlay2InjectedProps) => React.ReactNode);

export interface IOverlay2Props {
  /** When true, the panel will automatically attach itself to clicks on the previous sibling element */
  autoAttach?: boolean;

  /** Children - can be element or render function */
  children?: Overlay2Children;

  /** Additional CSS class */
  className?: string;

  /** Close overlay on escape key */
  closeOnEscape?: boolean;

  /** Container to render overlay into (portal) */
  container?: DOMContainer2;

  /** Placement of the overlay relative to target (the attachment property of the SdfFloatingPane */
  placement?: Placement2;

  /** Close overlay when clicking outside */
  rootClose?: boolean;

  /** Whether the overlay is visible */
  show?: boolean;

  /** Show arrow pointing to target */
  showArrow?: boolean;

  /** Additional styles */
  style?: React.CSSProperties;

  /** Target element to position relative to */
  target?: DOMContainer2;

  /** Callback when overlay finishes showing */
  onEntered?: () => void;

  /** Callback when overlay should hide */
  onHide?: () => void;

  /** Any other props passed to the floating pane */
  [key: string]: any;
}

/**
 * Resolve a DOMContainer2 reference to an HTMLElement
 */
function resolveDOMContainer(target: DOMContainer2): HTMLElement | null {
  logger('resolveDOMContainer(): Entering. target =', target);

  if (!target) {
    return null;
  }

  if (typeof target === 'function') {
    return target();
  }

  if ('current' in target) {
    return target.current;
  }

  return target;
}

/**
 * Overlay2 - Waypoint-based overlay component
 *
 * API-compatible with the original Overlay component, but uses Waypoint's
 * sdf-floating-pane underneath instead of react-overlays.
 */
export const Overlay2 = forwardRef<HTMLElement, IOverlay2Props>((props, ref) => {
  const {
    autoAttach,
    children,
    className,
    closeOnEscape = true,
    container,
    placement = 'bottom-start',
    rootClose = false,
    show = false,
    target,
    showArrow = false,
    style,
    onEntered,
    onHide,
    ...rest
  } = props;

  const paneRef = useRef<any>(null);
  const anchorElementRef = useRef<HTMLElement | null>(null);
  const [isShowing, setIsShowing] = useState(show);
  const [renderCounter, setRenderCounter] = useState(0);
  const [tabbableCount, setTabbableCount] = useState(0);

  logger('Overlay2(): Entering. show =', show);
  logger('Overlay2(): Entering. target =', target);
  logger('Overlay2(): Entering. isShowing =', isShowing);

  // Expose imperative methods
  useImperativeHandle(ref, () => paneRef.current, []);

  // Resolve target element
  useEffect(() => {
    anchorElementRef.current = resolveDOMContainer(target);
  }, [target]);

  // Use the state value to let React finish rendering
  // before we call the API to show the floating pane.
  useEffect(() => {
    setIsShowing(show);
  }, [show]);

  // Actually show/hide the floating pane by calling imperative API
  useEffect(() => {
    const pane = paneRef.current;
    let anchor = anchorElementRef.current;
    logger('Overlay2.useEffect(): Entering. pane =', pane);
    logger('Overlay2.useEffect(): Entering. anchor =', anchor);

    if (!pane) {
      return;
    }

    // If there is no explicit anchor element, use the previous sibling
    // to mimic the auto-attach behavior of the SdfFloatingPane
    if (!anchor) {
      anchor = pane.previousElementSibling;
      logger('Overlay2.useEffect(): Previous sibling anchor =', anchor);
    }

    if (isShowing) {
      // Opening
      logger('Overlay2.useEffect(): Calling pane.open', pane, anchor);

      pane.open(anchor)
        .then(() => {
          logger('Overlay2.useEffect(): Pane opened successfully');
          setTabbableCount(tabbable(pane.firstElementChild, { displayCheck: 'none', getShadowRoot: true, includeContainer: false })?.length ?? 0);
          setRenderCounter(renderCounter + 1);
          onEntered?.();
        })
        .catch((err: Error) => {
          console.warn('Overlay2.useEffect(): Failed to open floating pane:', err);
        });
    }
    else {
      // Closing
      logger('Overlay2.useEffect(): Calling pane.close', pane);

      pane.close()
        .then(() => {
          logger('Overlay2.useEffect(): Pane closed successfully');
          setRenderCounter(renderCounter + 1);
        })
        .catch((err: Error) => {
          console.warn('Overlay2.useEffect(): Failed to close floating pane:', err);
        });
    }
  }, [isShowing]);

  // Handle close event from Waypoint
  const handleAfterClose = useCallback((event?) => {
    logger('Overlay2.handleAfterClose(): Calling onHide', event);

    // Need to make sure the click event on the overlay trigger is processed first.
    setTimeout(() => {
      onHide?.(event);
    }, 20);
  }, [onHide]);

  // Try to detect click events from the close button in the Popover
  const handleClick = useCallback((event: any) => {
    logger('Overlay2.handleClick(): Event target =', event.target);

    // If the click was on the close button, eat the event and tell the OverlayTrigger
    // to close this overlay.
    if (event.target.tagName.toUpperCase() === 'SVG' || event.target.tagName.toUpperCase() === 'PATH' || event.target.classList.contains('vdl-popover__close')) {
      event.stopPropagation();
      event.preventDefault();
      onHide?.(event);
    }
  }, []);

  // Render children - handle function form, otherwise pass through
  const content = typeof children === 'function'
    ? children({
      placement,
      show,
      style,
      'aria-labelledby': rest['aria-labelledby']
    })
    : children;

  // The Popover has a caption that tabbable finds, so ignore it.
  const hasInteractiveContent = tabbableCount > 1;

  const sdfFloatingPaneProps: any = {
    'anchor-point': anchorElementRef.current,
    attachment: placement,
    'auto-attach': autoAttach,
    className,
    'capture-focus-on-open': hasInteractiveContent,
    'close-on-escape': closeOnEscape,
    'disable-close-on-click-outside': hasInteractiveContent,
    'portal-enabled': !!container,
    'position-context': 'ELEMENT',
    'show-arrow': showArrow,
    style,
    onClick: handleClick,
    onSdfAfterClose: handleAfterClose,
    ...rest
  };

  // Eliminate falsey props due to a bug in Stencil's React wrappers
  Object.keys(sdfFloatingPaneProps).forEach((key) => {
    if (!sdfFloatingPaneProps[key]) {
      delete sdfFloatingPaneProps[key];
    }
  });

  logger('Overlay2(): tabbableCount =', tabbableCount);
  logger('Overlay2(): sdfFloatingPaneProps =', sdfFloatingPaneProps);

  return (
    <SdfFloatingPane
      ref={paneRef}
      {...sdfFloatingPaneProps}
    >
      {content}
    </SdfFloatingPane>
  );
});

Overlay2.displayName = 'Overlay2';
